<template>
  <ion-page>
    <WhiteHeader
      :back-button="true"
      route-back="/time-reports"
      title="Rapport hebdo"
    />

    <ion-content class="ion-padding">
      <ion-row class="report-title">
        <div v-if="totalHours" class="report-week-total">
          {{ floatToString(totalHours, 'h') }}
        </div>
        {{ humanDates }}
      </ion-row>

      <DateSelector
        v-if="week"
        :days="computedDays"
        :day-totals="dayTotals"
        :statuses="statuses"
        :admin-changes="dayAdminChanges"
        :selected-day="currentDay"
        @onDateClick="onDateClick($event, 'time_reports')"
      />

      <HoursDetails
        v-if="currentDay && reports[currentDay]"
        :options="options"
        :disabled="statuses[currentDay] === 'full'"
        :current-day="currentDay"
        :day-details="dayDetails[currentDay]"
        :show-daterange="daterangeForHoursDetails"
        :time-reports-timer-mode="timeReportsTimerMode"
        v-model:details="reports[currentDay]['hours_details']['agents']"
        v-model:total-day="reports[currentDay]['hours_agent']"
        v-model:total-night="reports[currentDay]['hours_night_agent']"
        :has-admin-changes="reports[currentDay]['has_admin_changes']"
        :admin-total-day="reports[currentDay]['hours_admin']"
        :admin-total-night="reports[currentDay]['hours_night_admin']"
        :admin-comment="reports[currentDay]['comments_to_driver']"
        v-model:timer-details="
          reports[currentDay]['timer_hours_details']['agents']
        "
      />

      <div v-if="currentDay" class="mission-report-container">
        <ion-grid
          v-for="(question, key) in questions"
          :key="'question' + key"
          class="question"
        >
          <ion-grid v-for="field in question.fields" :key="field.property">
            <!-- title -->
            <h5>{{ field.name }}</h5>

            <!-- TEXTAREA -->
            <div
              v-if="
                field.kind === questionKinds.TEXTAREA && reports[currentDay]
              "
            >
              <Textarea
                v-if="field.property"
                :title="field.name"
                v-model:value="reports[currentDay][field.property]"
                :disabled="
                  statuses[currentDay] === 'full' || field.disabled === true
                "
              />
              <Textarea
                v-else-if="reports[currentDay].custom_questions"
                :title="field.name"
                v-model:value="
                  reports[currentDay].custom_questions[field.uniqid]
                "
                :disabled="
                  statuses[currentDay] === 'full' || field.disabled === true
                "
              />
            </div>

            <!-- BOOLEAN -->
            <YesNoSelector
              v-if="field.kind === questionKinds.BOOLEAN && reports[currentDay]"
              :title="field.name"
              :value="getValueForBooleanField(field, true)"
              @onValueChange="onBooleanChange($event, field)"
              :disabled="statuses[currentDay] === 'full'"
              :remove-null-option="true"
            />

            <!-- DATE -->
            <Date
              v-if="field.kind === questionKinds.DATE"
              :title="field.name"
              :selected-day="currentDay"
              v-model:date="reports[currentDay][field.property]"
              :disabled="statuses[currentDay] === 'full'"
            />

            <!-- DATERANGE -->
            <DateRange
              v-if="field.kind === questionKinds.DATERANGE"
              :title="field.name"
              :selected-day="currentDay"
              :dates="missionDays"
              v-model:start="reports[currentDay][field.property][0]"
              v-model:end="reports[currentDay][field.property][1]"
              :disabled="statuses[currentDay] === 'full'"
            />

            <!-- TIMESTAMP_NUMBER -->
            <TimestampNumber
              v-if="field.kind === questionKinds.TIMESTAMP_NUMBER"
              :title="field.name"
              :selected-day="currentDay"
              v-model:date="reports[currentDay][field.property + '_timestamp']"
              v-model:value="reports[currentDay][field.property]"
              :disabled="statuses[currentDay] === 'full'"
            />

            <!-- NUMBER -->
            <Number
              v-if="field.kind === questionKinds.NUMBER && field.uniqid"
              :title="field.name"
              :placeholders="field.placeholders"
              v-model:value="
                reports[currentDay]['custom_questions'][field.uniqid]
              "
              :disabled="statuses[currentDay] === 'full'"
            />
            <Number
              v-else-if="field.kind === questionKinds.NUMBER"
              :title="field.name"
              :placeholders="field.placeholders"
              v-model:value="reports[currentDay][field.property]"
              :disabled="statuses[currentDay] === 'full'"
            />

            <!-- When editing is disabled simply allow to consult existing files without adding new -->
            <AttachmentsList
              v-if="
                field.kind === questionKinds.FILE &&
                statuses[currentDay] === 'full'
              "
              :attachments="
                field.uniqid
                  ? reports[currentDay]['custom_questions'][field.uniqid]
                  : reports[currentDay][field.property]
              "
            />

            <!-- FILE -->
            <!-- if uniqid it's a custom question, else it's a "static" file type (e.g. "photo") -->
            <Attachments
              v-else-if="field.kind === questionKinds.FILE && field.uniqid"
              v-model:attachments="
                reports[currentDay]['custom_questions'][field.uniqid]
              "
              v-model:waiting-files="waitingFiles"
              v-model:attachments-to-delete="attachmentsToDelete"
              v-model:attachments-to-update="attachmentsToUpdate"
              type-vue="create"
              :files-too-big="false"
              :kind="field.uniqid"
              :title="field.name"
              :status="statuses[currentDay] === 'full' ? 'send' : 'create'"
            />
            <Attachments
              v-else-if="field.kind === questionKinds.FILE"
              v-model:attachments="reports[currentDay][field.property]"
              v-model:waiting-files="waitingFiles"
              v-model:attachments-to-delete="attachmentsToDelete"
              v-model:attachments-to-update="attachmentsToUpdate"
              type-vue="create"
              :files-too-big="false"
              :kind="field.property"
              :title="field.name"
              :status="statuses[currentDay] === 'full' ? 'send' : 'create'"
            />

            <!-- SELECT -->
            <ReportSelect
              v-if="field.kind === questionKinds.SELECT"
              :title="field.name"
              v-model:value="reports[currentDay][field.property]"
              :disabled="statuses[currentDay] === 'full'"
              :options="areas"
            />

            <!-- SUGGESTION -->
            <Suggestion
              v-if="field.kind === questionKinds.SUGGESTION"
              v-model:value="reports[currentDay][field.property]"
              :title="field.name"
              :disabled="statuses[currentDay] === 'full'"
              :suggestion="
                propertyHasSuggestion(field.property)
                  ? suggestions[currentDay][field.property]
                  : null
              "
            />
          </ion-grid>
        </ion-grid>
      </div>
    </ion-content>

    <div v-if="currentDay" class="bottom-buttons">
      <ion-button
        expand="block"
        fill="outline"
        class="save"
        @click="sendAs('draft')"
        :disabled="sending"
      >
        Enregistrer le brouillon
      </ion-button>

      <ion-button
        expand="block"
        class="next-step"
        @click="sendAs('sent')"
        :disabled="sending"
      >
        Valider et envoyer
      </ion-button>
    </div>
  </ion-page>
</template>

<script>
import { getParams } from '@/services/users';
import WhiteHeader from '@/components/driver/header/WhiteHeader';
import { chevronUpOutline, chevronDownOutline } from 'ionicons/icons';
import {
  IonPage,
  IonContent,
  IonButton,
  pickerController,
  IonGrid,
  IonRow,
  alertController,
} from '@ionic/vue';
import axios from 'axios';
import dayjs from 'dayjs';
import { makeWeek, formatDate } from '@/services/utils/reportUtils';
import { floatToString } from '@/services/utils/dateUtils';
import questionKinds from '@/services/constants/questionKinds';
import utils from '@/services/utils/Utils';
import DateSelector from '@/components/driver/rentalCoupon/report-components/DateSelector.vue';
import DateRange from '@/components/driver/rentalCoupon/report-components/DateRange.vue';
import Date from '@/components/driver/rentalCoupon/report-components/Date.vue';
import Textarea from '@/components/driver/rentalCoupon/report-components/Textarea.vue';
import TimestampNumber from '@/components/driver/rentalCoupon/report-components/TimestampNumber.vue';
import Number from '@/components/driver/rentalCoupon/report-components/Number.vue';
import ReportSelect from '@/components/driver/rentalCoupon/report-components/ReportSelect.vue';
import Suggestion from '@/components/driver/rentalCoupon/report-components/Suggestion.vue';
import Attachments from '@/components/driver/rentalCoupon/components/Attachments.vue';
import YesNoSelector from '@/components/driver/rentalCoupon/report-components/YesNoSelector.vue';
import { mixinReportCommon } from '@/components/driver/rentalCoupon/mixins/ReportCommon';
import AttachmentsList from '@/components/driver/detailMission/sub-components/AttachmentsList.vue';
import HoursDetails from './components/HoursDetails.vue';

const DAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const AREAS = utils.generateArray(15);

export default {
  name: 'TimeReportUpdate',

  components: {
    IonPage,
    IonContent,
    IonButton,
    IonGrid,
    IonRow,

    // custom components
    WhiteHeader,
    DateSelector,
    HoursDetails,

    // question components
    DateRange,
    Date,
    Textarea,
    TimestampNumber,
    Number,
    ReportSelect,
    Suggestion,
    Attachments,
    AttachmentsList,
    YesNoSelector,
  },

  mixins: [mixinReportCommon],

  setup() {
    return {
      chevronUpOutline,
      chevronDownOutline,
      questionKinds,
    };
  },

  data() {
    return {
      week: null,
      days: DAYS,
      areas: AREAS,
      dates: [],
      computedDays: {},
      questions: {},
      reports: {},
      currentDay: null,
      options: [],
      dayDetails: {},
      sending: false,
      suggestions: {},
      daterangeForHoursDetails: false,
      timeReportsTimerMode: false,

      // files
      waitingFiles: [],
      attachmentsToDelete: [],
      attachmentsToUpdate: {},
    };
  },

  mounted() {
    const start = dayjs(this.$route.params.date).startOf('week');
    DAYS.forEach((day, index) => {
      this.dates.push(start.add(index, 'days').format('YYYY-MM-DD'));
    });
    this.week = makeWeek(start);

    const formatted = {};
    Object.values(this.dates).forEach(day => {
      formatted[this.formatDate(day)] = day;
    });
    this.computedDays = formatted;

    // Set currentDay to the clicked day from params
    this.currentDay = this.$route.params.date;
  },

  ionViewWillEnter() {
    this.fetchDetail();
    // to ensure fully compatibily, move new "custom" elements into separate fetch call
    this.fetchCustomOptions();
  },

  methods: {
    async fetchDetail() {
      axios
        .get(
          `/driver/time-report/${this.$route.params.date}`,
          await getParams(),
        )
        .then(response => {
          this.reports = response.data;

          // if current day and no hours details, initialize it
          this.onDateClick(this.currentDay || this.dates[0], 'time_reports');
        })
        .finally(() => {
          this.sending = false;
        });
    },

    async fetchCustomOptions() {
      const config = await getParams();
      config.params = {
        week: this.$route.params.date,
      };

      axios.get('/driver/time-report-options', config).then(response => {
        this.questions = response.data.questions;
        this.options = response.data.options;
        this.dayDetails = response.data.day_details;
        this.daterangeForHoursDetails =
          response.data.daterange_for_hours_details;
        this.timeReportsTimerMode = response.data.time_reports_timer_mode;

        // if current day and no hours details, initialize it
        this.onDateClick(this.currentDay || this.dates[0], 'time_reports');
      });
    },

    /**
     * When day changed, try to get suggestions if any
     */
    async getSuggestions() {
      // don't do nothing if we already have suggestions for this day
      if (this.suggestions[this.currentDay]) return;

      const config = await getParams();
      config.params = {
        day: this.currentDay,
      };

      axios
        .get('/driver/time-report-suggestions', config)
        .then(response => {
          // foreach suggestions, set data
          Object.entries(response.data).forEach(([key, value]) => {
            if (!this.suggestions[this.currentDay]) {
              this.suggestions[this.currentDay] = {};
            }

            this.suggestions[this.currentDay][key] = value;
          });
        })
        .catch(() => {
          // on error, just set empty suggestions for this day
          this.suggestions[this.currentDay] = {};
        });
    },

    formatDate,

    hasSetArea(report) {
      // if report has already been sent for this date, consider set
      if (this.statuses && this.statuses[report.date] === 'full') return true;

      // if no suggestions for this date, consider set
      if (!this.suggestions || !this.suggestions[report.date]) return true;

      const areaSuggestion = this.suggestions[report.date].area_worksite_agent;
      if (!areaSuggestion) return true;

      // if we have a suggestion, check if it's set
      return report.area_worksite_agent === areaSuggestion;
    },

    /**
     * Ask user to confirm if he want to send report, even though areas are not fully set
     */
    async confirmAreas(dates) {
      const formattedDates = Object.entries(this.computedDays)
        .map(([key, value]) => {
          if (dates.includes(value)) return '- ' + key;
          return null;
        })
        .filter(v => v !== null)
        .join('<br/>');

      const alert = await alertController.create({
        header: 'Attention',
        message:
          "Vous n'avez pas validé la zone chantier pour les jours suivants :<br/>" +
          formattedDates +
          '<br/>Souhaitez-vous poursuivre ?',
        buttons: [
          {
            text: 'Annuler',
            role: 'cancel',
          },
          {
            text: 'Confirmer',
            role: 'confirm',
          },
        ],
      });

      await alert.present();
      const { role } = await alert.onDidDismiss();
      return role === 'confirm';
    },

    async sendAs(status) {
      const dataToSend = this.getAllDataToSend('time_reports');
      const reportData = JSON.parse(dataToSend.get('report_data'));

      // filter out empty reports
      const newReports = [];
      let missingAreas = [];

      reportData.entries.forEach(report => {
        const status = this.getCompletionStatuses(report);

        // if we have a suggestion that hasn't been set, we will send a confirm message
        if (!this.hasSetArea(report)) {
          missingAreas.push(report.date);
        }

        // don't send empty reports
        if (status !== 'empty') {
          newReports.push(report);
          return;
        }

        // if we have hours agents or night agents, we consider the report as sendable
        if (report.hours_agent || report.hours_night_agent) {
          newReports.push(report);
          return;
        }

        // if customfields fullfilled check is empty, check hours details
        // if we have hours details, we consider the report as sendable
        if (
          report.hours_details &&
          report.hours_details.agents &&
          report.hours_details.agents.length
        ) {
          newReports.push(report);
        }
      });

      if (missingAreas.length && !(await this.confirmAreas(missingAreas))) {
        return;
      }

      // if no report to send, do nothing
      if (!newReports.length) {
        this.sendToast('Aucun rapport à envoyer.', 'danger');
        return;
      }

      // ask confirmation if we want to send reports
      // also allow user to only send part of the days, and keep the others in draft
      if (status === 'sent') {
        const daysToSend = newReports.map(report => report.date);
        const selectedDays = await this.confirm(daysToSend);

        // If user cancelled or didn't select any days
        if (selectedDays === null) return;

        // note that if user didn't select any days, we still send them
        // but only with 'draft' status

        // Set status for each report based on selection
        reportData.entries = newReports.map(report => ({
          ...report,
          status: selectedDays.includes(report.date) ? 'sent' : 'draft',
        }));
      } else {
        reportData.entries = newReports;
      }

      dataToSend.set('report_data', JSON.stringify(reportData));

      this.sending = true;
      try {
        await axios.post(`/driver/time-report`, dataToSend, await getParams());

        this.sendToast(
          status === 'draft'
            ? 'OK, données enregistrées en brouillon.'
            : 'OK, données envoyées.',
        );
      } catch {
        this.sendToast("Erreur lors de l'envoi.", 'danger');
      }

      this.fetchDetail();
    },

    async openPicker(report) {
      const selectedDates =
        report.hours_agent !== 0 ? report.hours_agent.split(':') : ['00', '00'];

      const picker = await pickerController.create({
        columns: utils.generatePickerColumns(
          selectedDates[0],
          selectedDates[1],
        ),
        buttons: [
          {
            text: 'Annuler',
            role: 'cancel',
          },
          {
            text: 'Valider',
            handler: value => {
              report.hours_agent = [
                value.hours.value,
                value.minutes.value,
              ].join(':');
            },
          },
        ],
      });
      await picker.present();
    },

    propertyHasSuggestion(property) {
      return (
        this.suggestions &&
        this.suggestions[this.currentDay] &&
        this.suggestions[this.currentDay][property]
      );
    },

    floatToString,
  },

  computed: {
    humanDates() {
      if (!this.week) return '';

      return `Du ${this.week.formattedStart} au ${this.week.formattedEnd}`;
    },

    statuses() {
      if (!this.reports || !Object.keys(this.reports)) return {};

      const computedStatuses = {};

      Object.entries(this.reports).forEach(([day, report]) => {
        let status = report.status === 'sent' ? 'full' : 'partial';
        if (!report.status) {
          status = 'empty';
        }

        computedStatuses[day] = status;
      });

      return computedStatuses;
    },

    // Total hours per day, to be passed to DateSelector
    dayTotals() {
      const totals = {};
      if (!this.reports || !Object.keys(this.reports).length) return totals;

      Object.entries(this.reports).forEach(([day, report]) => {
        if (report.status !== 'sent') return;
        totals[day] = report.hours_agent_total || 0;
      });

      return totals;
    },

    // Same admin-change checks as the HoursDetails card, per day
    dayAdminChanges() {
      const changes = {};

      Object.entries(this.reports).forEach(([day, report]) => {
        changes[day] = report.has_admin_changes;
      });

      return changes;
    },

    // Total hours for the week
    totalHours() {
      let output = 0;
      if (!this.reports || !Object.keys(this.reports).length) return output;

      Object.keys(this.reports).forEach(dateKey => {
        const dayReport = this.reports[dateKey];
        if (dayReport.status !== 'sent') return;
        // Contrary to `ReportList`, no need to find the good week, since there is only one
        output += dayReport.hours_agent_total || 0;
      });

      return output;
    },
  },

  watch: {
    currentDay() {
      this.getSuggestions();
    },
  },
};
</script>

<style scoped lang="scss">
ion-content {
  --padding-start: 0;
  --padding-end: 0;
  --padding-bottom: 0;
  --padding-top: 0;
  --background: #fffcfa;
}

.report-title {
  display: flex;
  align-items: center;
}

.report-week-total {
  margin-right: 0.75rem;
  transform: scale(0.9);
}

.mission-report-container {
  padding: 0 16px;
  padding-bottom: 100px; /* Add padding to prevent content from being hidden behind bottom bar */
}

h3 {
  margin-top: 32px;
  font-size: 16px;
  font-weight: bold;
}

.question {
  padding: 0 8px;
}

.h-100 {
  height: 100%;
}
</style>
